import React, { Component } from "react";
import Router from "next/router";
import _ from "lodash";
import Cookie from "js-cookie";
import uuid4 from "uuid4";
import { getDisplayName } from "@/utils/HOC";
import type { PageConfig } from "@/routes";
import routes from "@/routes";
import config from "@/configs";
import { isMobile } from "@/utils/device";
import type { AppProps } from "next/app";
import { getUtm } from "@/utils/utm";
import { CR_SESSION_ID } from "@/constants/cookieAndLsName";

export type QDReportProps<P = {}> = AppProps<P> & {
  router: {
    query: any;
    pathname: string;
  };
  QDReportSender: Function;
};

interface State {
  pageConfig: ReportDataProps;
}

export interface ReportDataProps {
  eid?: string;
  uiname?: string;
  etype?: string;
  pn?: string;
  pdt?: string;
  pdid?: string;
  pagetitle?: string | number;
  pagecate?: string;
  block_pos?: string;
  blocktitle?: string | number;
  blocktype?: string;
  pos?: string | number;
  dt?: string | number;
  did?: string | number;
  param?: object | string;
  alg?: string;
  dl?: string | number;
}

export const formatParam = (param: string | object) => {
  if (typeof param === "string") {
    return param;
  }
  return JSON.stringify(param);
};

interface ReportDomDataProps {
  "data-report-eid": string;
  "data-report-uiname": string; // dom上大小写不敏感
}

declare global {
  interface Window {
    QDReportSender: Function;
  }
}
export const initSession = () => {
  document.addEventListener("visibilitychange", () => {
    // 进入离开，都重置sid
    const crtValue = Cookie.get(CR_SESSION_ID);
    if (crtValue && uuid4.valid(crtValue)) {
      Cookie.set(CR_SESSION_ID, crtValue, { expires: 1 / 24 / 2 }); // 过期时间 30分钟
    } else {
      Cookie.set(CR_SESSION_ID, uuid4(), { expires: 1 / 24 / 2 }); // 过期时间 30分钟
    }
  });
};

export const QDReportSender = (reportData: ReportDataProps) => {
  if (_.isEmpty(reportData)) return;
  // console.log("QDReportSender", JSON.stringify(reportData));
  if (typeof window !== "undefined") {
    typeof window.QDReportSender === "function" &&
      window.QDReportSender({}, reportData);
  }
};

export const toDomProps = (obj: ReportDataProps) => {
  if (!obj) {
    return {};
  }
  let formatObj: any = {};
  (Object.keys(obj) as (keyof ReportDataProps)[]).forEach((key) => {
    let domKey = `data-report-${key}` as keyof ReportDomDataProps;
    let value = obj[key];
    if (
      (key === "param" && typeof value === "object") ||
      typeof value === "string"
    ) {
      value = formatParam(value);
    }
    formatObj[domKey.toLocaleLowerCase()] = value;
  });
  return formatObj;
};

export const getNodeReportInfo = (node: HTMLElement) => {
  const regexpReport = /^data-?report-?(.*)$/i;
  let rs: ReportDataProps = { eid: "", uiname: "" };
  for (const name of node.getAttributeNames()) {
    const value = node.getAttribute(name);
    const matchResult = name.match(regexpReport);
    if (matchResult && matchResult.length > 1 && value) {
      let key = matchResult[1] as keyof ReportDataProps;
      rs[key] = value;
    }
  }
  return rs;
};

/**
 * 判断 dom 是否携带上报信息，如果是，则可以基于该 element 的 attributes 进行上报
 * 这是一个简易做法，因为 eid 和 uiname 是上报的必要字段
 * @param element
 * @returns {*|boolean}
 */
const isReportDom = (element: EventTarget) => {
  if (!element) {
    return false;
  }
  if (element instanceof HTMLElement) {
    return (
      !!element.getAttribute("data-report-eid") ||
      !!element.getAttribute("data-report-uiname")
    );
  }
  return false;
};
/**
 * 获取正确的上报url
 * @method getUrl
 * @param url
 * @param params
 * @returns {*}
 */
const getUrl = (url: string, params: ReportDataProps) => {
  const keys: string[] = [];
  let concatUrl = url;
  let _params = {
    ...params,
    uuid: Cookie.get("webnovel_uuid") || "",
    guid: Cookie.get("guid") || "",
    sid: Cookie.get(CR_SESSION_ID) || "",
    logtime: +new Date(),
  };
  if (_params.param) {
    _params.param = formatParam(_params.param);
  }
  _.forEach(_params, (value, key) => {
    keys.push(`${key}=${encodeURIComponent(value)}`);
  });
  if (url.indexOf("?") !== -1) {
    concatUrl += `&${keys.join("&")}`;
  } else {
    concatUrl += `?${keys.join("&")}`;
  }
  return concatUrl;
};

/**
 * 创建发送请求器
 * @method createSender
 * @param url
 */
const createSender = (url: string) => {
  // 能用beacon api, 尽量用beacon api
  // 使用浏览器能力，即使切换页面也能让上报请求到达后台
  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, "");
  } else {
    let img: HTMLImageElement | null = new Image();
    img.onload = () => {
      img = null;
    };
    img.onerror = () => {
      img = null;
    };
    img.src = url;
  }
};

/**
 * import QDReport from 'components/QDReport';
 * exports QDReport(SSR(Page), { pid: 'page id' || function });
 * @example
 */

export default function QDReport<T extends QDReportProps = QDReportProps>(
  Page: React.ComponentType<T>
) {
  const REPORT_CONF = {
    path: "qdhw",
    cname: "crpclog", // 按域名区分
    platform: "pc", // 按域名区分
    device: isMobile() ? "mobile" : "desktop", // 按设备
    cgi:
      config.envType === "pro"
        ? "//openlog.webnovel.com/web"
        : "//oaopenlog.webnovel.com/web",
  };

  class Report extends Component<QDReportProps, State> {
    initial: number = 0;
    rootEl: HTMLElement | null = null;

    constructor(props: QDReportProps) {
      super(props);
      if (typeof window !== "undefined") {
        this.init();
        this.send = this.send.bind(this);
        // set to global for paymentCallback/index.js use
        window.QDReportSender = this.send;
      }
    }

    delegateCb: (this: HTMLElement, ev: MouseEvent) => any | null = () => {};

    componentDidMount() {
      this.initSend();
      Router.events.on("routeChangeStart", this.uninstall);
      Router.events.on("routeChangeComplete", this.handleRouteInit); // todo: fix shallow 时 也被调用的问题
    }

    // remove event when component unmount
    componentWillUnmount() {
      Router.events.off("routeChangeStart", this.uninstall);
      Router.events.off("routeChangeComplete", this.handleRouteInit);
      this.uninstall();
    }

    uninstall = () => {
      const { rootEl } = this;
      if (!rootEl) {
        return;
      }
      if (this.delegateCb !== null) {
        rootEl.removeEventListener("click", this.delegateCb, false);
      }
      this.delegateCb = () => {};
    };

    handleRouteInit = () => {
      this.init();
      this.initSend();
    };

    init = () => {
      const { router } = this.props;
      const { query = {}, pathname = "" } = router;

      const utmParams = getUtm();

      // 需要在浏览器当中才能读到的值
      const browserCommons = {
        // 当前页面url
        url: window.location.href,
        // 来源
        ref: document.referrer,
        // 分辨率横屏
        sw: window.screen.width,
        // 分辨率竖屏
        sh: window.screen.height,
        // 页面标题
        title: document.title,
        ua: navigator.userAgent,
        ...utmParams,
      };

      Cookie.set("QDReport_referrer", window.location.href, {
        expires: 1 / 24,
        path: "/",
      });

      // 因为是单页的，所以没有referrer要进行处理一下

      // 有一些pid是动态的，所以也支持传入的是function
      const config: PageConfig | undefined = routes.find(
        (item: PageConfig) => item.path === pathname
      );
      const { reportParams, pageId, manualReportPV } = config || {};
      let pn = pageId;
      // 从路由中获取一些参数
      const configFromRouter = reportParams ? reportParams(query) : {};
      const pageConfig = _.merge(
        {},
        { pn, ...configFromRouter, ...browserCommons }
      );
      // ts-ignore
      this.state = { pageConfig }; // eslint-disable-line react/no-direct-mutation-state

      // first report pageview
      // 当且仅当存在pn的时候才进行pv的上报
      if (pn && !manualReportPV) {
        this.reportPageView();
      }
    };

    /**
     * 初始化dataReport的click事件上报
     * @method initSend
     */
    initSend() {
      const { rootEl } = this;
      this.initial = 0;
      this.delegateCb = (e) => {
        const { rootEl } = this;
        let { target } = e;
        let delegate = false;
        if (!target || !(target instanceof HTMLElement) || !target.attributes) {
          return;
        }
        // 从 target 开始往上寻找携带 report 信息的 dom，则将触发send的事件
        do {
          if (isReportDom(target!)) {
            delegate = true;
            break;
          }
          if (target !== rootEl && target instanceof HTMLElement) {
            target = target.parentNode;
          }
        } while (target !== rootEl);
        // if triggered from delegate node, send(e)
        if (delegate) {
          this.send(e, undefined);
        }
      };
      rootEl?.addEventListener("click", this.delegateCb, false);
    }

    /**
     * @method 上报点击数据
     * @param e
     * @param params
     */
    send(e: MouseEvent, params = {}) {
      const { cgi, ...conf } = REPORT_CONF;
      const now = +new Date();
      const { target = null } = e;
      // 有target并且100ms以内的重复上报可以忽略
      // 无target的不限制
      if (target && now - this.initial < 100) {
        return;
      }
      if (target) {
        this.initial = now;
      }
      const defaults = { etype: "A" };
      let obj = _.merge({}, conf, this.state.pageConfig, defaults, params);

      let currentElement = target;
      while (
        currentElement instanceof HTMLElement &&
        currentElement.attributes
      ) {
        obj = _.merge({}, obj, getNodeReportInfo(currentElement));
        // 从 target 开始往上寻找携带 report 信息的 dom，则将触发send的事件
        if (isReportDom(currentElement)) {
          break;
        }
        currentElement = currentElement.parentNode;
      }
      // 如果没有 eid，则拼装获取
      if (!obj.eid) {
        obj.eid = `qi_${obj.etype}_${obj.pn}_${obj.uiname}`;
      }
      createSender(getUrl(cgi, obj));
    }

    /**
     * 上报pv数据
     * @method reportPageView
     */
    reportPageView() {
      const { cgi, ...conf } = REPORT_CONF;
      const { pageConfig } = this.state;
      const options = _.merge(
        {},
        conf,
        {
          eid: `qi_P_${pageConfig.pn}`, // 加上兜底的 eid
          etype: "P",
          is_fanfic_page: 0,
        },
        pageConfig
      );
      // send pageview
      createSender(getUrl(cgi, options));
    }

    render() {
      return (
        <div
          id={"qd-report-root"}
          ref={(c) => {
            this.rootEl = c;
          }}
        >
          <Page {...(this.props as T)} QDReportSender={this.send} />
        </div>
      );
    }

    public static displayName = `withQDReport(${getDisplayName(Page)})`;
  }

  return Report;
}
